import { MenuItem, Paper, Popper, PopperPlacementType } from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import React, { useEffect, useRef } from 'react'

import { useStyles } from './styles'

type Placement = 'left' | 'right' | 'top' | 'bottom'

export interface NestedMenuProps {
  children: React.ReactNode
  title: string
  placement?: Placement
  minWidth?: string | number
  onOpen?: () => void
  onClose?: () => void
  disabled?: boolean
  menuItemClassName?: string
  popperClassName?: string
  paperClassName?: string
  icon?: React.ReactNode
  showIcon?: boolean
  autoPlacement?: boolean
}

export interface NestedMenuState {
  anchorEl: HTMLElement | null
  isClicked: boolean
}

export interface StyleProps {
  isOpen: boolean
  minWidth?: string | number
}

export const CLOSE_ALL_EVENT = 'nested-menu:close-all' as const

export type CloseAllEvent = CustomEvent<{
  source: HTMLElement | null
}>

export function NestedMenu({
  children,
  title,
  placement = 'right',
  minWidth = '27.8rem',
  onOpen,
  onClose,
  disabled = false,
  menuItemClassName,
  popperClassName,
  paperClassName,
  icon,
  showIcon = true,
  autoPlacement = true,
}: NestedMenuProps) {
  const [menuState, setMenuState] = React.useState<NestedMenuState>({
    anchorEl: null,
    isClicked: false,
  })

  const menuRef = useRef<HTMLElement>(null)
  const isOpen = Boolean(menuState.anchorEl) || menuState.isClicked
  const classes = useStyles({ isOpen, minWidth })

  const getSmartPlacement = (): PopperPlacementType => {
    if (!autoPlacement || !menuState.anchorEl) return placement

    const rect = menuState.anchorEl.getBoundingClientRect()
    const childrenCount = React.Children.count(children)

    const menuItemHeight = rect.height || 48
    const estimatedMenuHeight = childrenCount * menuItemHeight + 16

    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    if (spaceBelow >= estimatedMenuHeight) {
      return `${placement}-start` as PopperPlacementType
    }

    if (spaceAbove >= estimatedMenuHeight) {
      return `${placement}-end` as PopperPlacementType
    }

    return placement
  }

  const closeMenu = () => {
    setMenuState({ anchorEl: null, isClicked: false })
    onClose?.()
  }

  const openMenu = (element: HTMLElement, isClick: boolean = false) => {
    if (disabled) return

    document.dispatchEvent(
      new CustomEvent(CLOSE_ALL_EVENT, {
        detail: { source: menuRef.current },
      }),
    )
    setMenuState({ anchorEl: element, isClicked: isClick })
    onOpen?.()
  }

  useEffect(() => {
    const handleCloseAll = (event: Event) => {
      const customEvent = event as CloseAllEvent
      if (!menuRef.current || customEvent.detail?.source === menuRef.current)
        return
      closeMenu()
    }

    document.addEventListener(CLOSE_ALL_EVENT, handleCloseAll)
    return () => document.removeEventListener(CLOSE_ALL_EVENT, handleCloseAll)
  }, [])

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return

    if (!menuState.isClicked) {
      openMenu(e.currentTarget)
    }
    if (menuState.anchorEl) {
      setMenuState({
        anchorEl: menuState.anchorEl,
        isClicked: menuState.isClicked,
      })
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return

    menuState.isClicked ? closeMenu() : openMenu(e.currentTarget, true)
  }

  const handleMouseLeave = () => {
    if (!menuState.isClicked) {
      closeMenu()
    }
  }

  const defaultIcon = (
    <ArrowRightIcon fontSize='medium' color='action' viewBox='-8 0 24 24' />
  )
  const menuIcon = icon || defaultIcon

  return (
    <>
      <MenuItem
        ref={menuRef as React.RefObject<HTMLLIElement>}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className={`${classes.menuItem} ${menuItemClassName || ''}`}
        disabled={disabled}
      >
        {title}
        {showIcon && menuIcon}
      </MenuItem>
      <Popper
        open={isOpen}
        anchorEl={menuState.anchorEl}
        placement={getSmartPlacement()}
        disablePortal={false}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`${classes.popper} ${popperClassName || ''}`}
      >
        <Paper className={`${classes.paper} ${paperClassName || ''}`}>
          {children}
        </Paper>
      </Popper>
    </>
  )
}
